/* AF - Odometer price: digits spin and settle on a new amount.
   - window.AFOdometer.render(host, value, prevValue): builds the spinning digits
     into `host` (used by the sticky Add to Cart bar).
   - Main product price: after a variant change, the visible price spins from
     the old amount to the new one, then its markup is put back so anything
     reading the price text sees "৳567", never the digit strips. While a spin
     runs, the element carries the clean text in data-af-price. */
(() => {
  if (window.AFOdometer) return;

  const H = 1.2; // row height in em; matches .af-odo line-height
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const toNumber = (s) => parseFloat(String(s || '').replace(/[^\d.]/g, ''));

  /* Each digit is a clipped column over a 0-9 strip (repeated three times).
     Changed digits spin from the old digit to the new one (upward when the
     price rises, downward when it drops) and settle with a slight overshoot,
     left digit first.
     Geometry is measured, not assumed: the strip is 30 block rows, and the
     column height, width and every offset are the rendered row size in px.
     In-app browsers (Facebook/Instagram WebView) can enlarge text on their
     own; with em maths and a wrapped text strip the rows then drifted out of
     their window and neighbouring digits showed mid-spin. */
  function render(host, value, prevValue, onDone) {
    const animate = !!prevValue && prevValue !== value && !reduceMotion();
    const up = toNumber(value) >= toNumber(prevValue);
    const prevDigits = String(prevValue || '').replace(/\D/g, '');
    const digitCount = value.replace(/\D/g, '').length;

    const wrap = document.createElement('span');
    wrap.className = 'af-odo';
    wrap.setAttribute('aria-hidden', 'true');
    host.appendChild(wrap);
    const wrapLS = getComputedStyle(wrap).letterSpacing;

    const moves = [];
    let rowPx = 0;
    let di = 0;
    for (const ch of value) {
      if (!/\d/.test(ch)) {
        const sep = document.createElement('span');
        sep.className = 'af-odo__sep';
        sep.textContent = ch;
        wrap.appendChild(sep);
        continue;
      }
      const to = +ch;
      // align old digits to the right (363 -> 1,220 compares 0363 with 1220)
      const pi = prevDigits.length - (digitCount - di);
      const from = pi >= 0 ? +prevDigits[pi] : 0;
      const col = document.createElement('span');
      col.className = 'af-odo__col';
      const strip = document.createElement('span');
      strip.className = 'af-odo__strip';
      for (let r = 0; r < 30; r++) {
        const row = document.createElement('span');
        row.className = 'af-odo__row';
        row.textContent = String(r % 10);
        strip.appendChild(row);
      }
      col.appendChild(strip);
      wrap.appendChild(col);

      // Measured inside the strip, so any text enlargement applies to both.
      // Each column is as wide as its final digit (Urbanist digits are
      // proportional), so the settled number matches the plain text after it.
      const probe = document.createElement('span');
      probe.className = 'af-odo__probe';
      probe.style.letterSpacing = wrapLS; // the strip zeroes it; the plain price has it
      probe.textContent = ch;
      strip.appendChild(probe);
      const w = probe.getBoundingClientRect().width;
      probe.remove();
      const h = strip.firstChild.getBoundingClientRect().height;
      if (h) rowPx = h;
      if (w) col.style.width = `${w}px`;
      if (rowPx) col.style.height = `${rowPx}px`;

      const off = (n) => (rowPx ? `${-n * rowPx}px` : `${-n * H}em`);
      let target = 10 + to;
      if (animate && from !== to) {
        target = up ? (to > from ? 10 + to : 20 + to) : (to < from ? 10 + to : to);
        strip.style.transform = `translateY(${off(10 + from)})`;
        moves.push([strip, off(target), di]);
      } else {
        strip.style.transform = `translateY(${off(target)})`;
      }
      di++;
    }
    if (rowPx) wrap.style.height = `${rowPx}px`;

    // In running text (not a flex host), centre the digit window on the line
    // box, taking exactly one line's height so the line never grows.
    const hostCS = getComputedStyle(host);
    const lineH = parseFloat(hostCS.lineHeight);
    const inText = !/flex|grid/.test(hostCS.display);
    if (inText && lineH) {
      const m = `${(lineH - (rowPx || H * parseFloat(hostCS.fontSize))) / 2}px`;
      wrap.style.verticalAlign = 'top';
      wrap.style.marginTop = m;
      wrap.style.marginBottom = m;
    }

    // Then put the digits exactly where plain text sits: measure a hidden "0"
    // beside them and shift by the difference, so the spin lines up with the
    // plain price that replaces it, in any browser (in-app ones resize text).
    // Running text only: a flex host (the sticky bar's price tab) centres the
    // digits and its currency sign is placed against that centre.
    const firstCol = inText && wrap.querySelector('.af-odo__col');
    if (firstCol) {
      const ref = document.createElement('span');
      ref.textContent = '0';
      ref.style.visibility = 'hidden';
      host.insertBefore(ref, wrap);
      const glyphTop = (node) => {
        const r = document.createRange();
        r.selectNodeContents(node);
        return r.getBoundingClientRect().top;
      };
      const row = firstCol.querySelector('.af-odo__row');
      const inRow = glyphTop(row.firstChild) - row.getBoundingClientRect().top;
      const d = glyphTop(ref.firstChild) - (firstCol.getBoundingClientRect().top + inRow);
      ref.remove();
      if (Math.abs(d) > 0.25) {
        wrap.style.position = 'relative';
        wrap.style.top = `${d}px`;
      }
    }

    if (!moves.length) { if (onDone) onDone(); return; }
    // Finish when the last digit lands (fallback timer in case transitionend
    // never fires, e.g. the tab is hidden).
    let done = false;
    const finish = () => { if (done) return; done = true; if (onDone) onDone(); };
    const lastStrip = moves[moves.length - 1][0];
    lastStrip.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 750 + moves[moves.length - 1][2] * 70 + 400);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      moves.forEach(([strip, offset, i]) => {
        strip.style.transitionDelay = `${i * 70}ms`;
        strip.classList.add('is-spinning');
        strip.style.transform = `translateY(${offset})`;
      });
    }));
  }

  window.AFOdometer = { render };

  /* ---------- main product price ---------- */
  const visiblePrice = (sectionId) => {
    const pr = document.querySelector(`#price-${sectionId} .price`);
    if (!pr) return null;
    return pr.classList.contains('price--on-sale')
      ? pr.querySelector('.price__sale .price-item--last')
      : pr.querySelector('.price__regular .price-item--regular');
  };
  const last = new Map();

  function spin(item, text, prevText) {
    const m = text.match(/^([^\d]*)([\d][\d.,]*)(.*)$/);
    const prevAmount = (prevText.match(/[\d][\d.,]*/) || [''])[0];
    if (!m || !prevAmount) return;
    // Readers (e.g. the sticky bar) use data-af-price while the digits spin.
    // The buy card's price wraps the ৳ in a thin .af-tk span: keep it during
    // the spin and put the server's markup back afterwards.
    const html = item.innerHTML;
    const tk = item.querySelector('.af-tk');
    item.dataset.afPrice = text;
    item.textContent = '';
    if (tk) item.appendChild(tk.cloneNode(true));
    else if (m[1]) item.appendChild(document.createTextNode(m[1]));
    render(item, m[2], prevAmount, () => {
      // back to the plain price (unless the theme already replaced this node)
      if (item.isConnected && item.querySelector('.af-odo')) item.innerHTML = html;
      delete item.dataset.afPrice;
    });
    if (m[3]) item.appendChild(document.createTextNode(m[3]));
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = text;
    item.appendChild(sr);
  }

  // product-info.js swaps the price box's HTML a few hundred ms before it
  // publishes variantChange. Mutation callbacks run before the next paint, so
  // reacting here means the new plain price never flashes before the spin.
  function check(sid) {
    const item = visiblePrice(sid);
    if (!item || item.querySelector('.af-odo')) return; // our own spin in progress
    const text = item.textContent.trim();
    const prev = last.get(sid);
    last.set(sid, text);
    if (prev && prev !== text && !reduceMotion()) spin(item, text, prev);
  }

  const watched = new WeakSet();
  function watch() {
    document.querySelectorAll('[id^="price-"]').forEach((box) => {
      const sid = box.id.slice('price-'.length);
      if (!last.has(sid)) {
        const item = visiblePrice(sid);
        if (item) last.set(sid, item.textContent.trim());
      }
      if (watched.has(box)) return;
      watched.add(box);
      new MutationObserver(() => check(sid)).observe(box, { childList: true, subtree: true });
    });
  }

  const start = () => {
    watch();
    // If a whole section is re-rendered, pick up its new price box.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(watch));
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
